import { useEffect, useLayoutEffect, useState } from 'react';

import type { CoreExactMutation, WorkforceOperationalSessionResult } from '@exylon/generated-contract-kernel/types';
import { createOperationalPresenceHeartbeat } from './operational-presence-heartbeat';
import type { PanelScreenConnection } from '../foundation/runtime-port';

/**
 * I-19 (TPV-FLOW-AUDIT-2026-09-23) · the one scoped runtime of the live service
 * screens: TPV, Servicio, Cocina and History build on it (Q-17, row 3.12a).
 *
 * A scope is one React owner (the screen keys it by port, restaurant and
 * principal). Inside it a *generation* (`epoch`) retires every local result at
 * once: a disconnect, a real unmount or a screen's own reset bumps it, and a
 * request, a presented revision or a single-flight attempt of an older
 * generation can never be applied.
 *
 * docs/decisions/2026-10-06-panel-read-runtime.md · a foreground Identity
 * verification is not a disconnect: the generation is kept and a read on the
 * wire keeps its answer. Only a real disconnect (offline, or a session that is
 * not ready), a real unmount or the screen's own reset retires it. A retired
 * read is aborted on the network through the signal `guard` hands it; a
 * dispatched mutation is never cancelled and no server rollback is claimed.
 *
 * The screen keeps its domain: what to read, how to project it, which copy to
 * show. The runtime owns the fences every screen needs the same way.
 */
type ScreenConnection = PanelScreenConnection;

/** How long an opening call waits for a frozen scope to come back before it
 * reports the honest failure surface instead of waiting on a silent screen. */
const SCREEN_OPEN_RESUME_MS = 10_000;

// Opaque React instance keys. They confer no authority and retain no port,
// principal, snapshot or other business data strongly.
const instanceKeys = new WeakMap<object, number>();
let nextInstanceKey = 0;
export function scopeInstanceKey(owner: object): number {
  let key = instanceKeys.get(owner);
  if (key === undefined) {
    key = ++nextInstanceKey;
    instanceKeys.set(owner, key);
  }
  return key;
}

interface ScopedScreenRuntimeOptions {
  /** The error every fenced completion rejects with; it names the screen. */
  readonly discarded: () => Error;
  /** The composition's connectivity at mount. */
  readonly connection?: ScreenConnection;
  /** Admission the screen adds to mount, generation and connection (History:
   * the session and its exact read grant). */
  readonly admit?: () => boolean;
  /** Screen state that belongs to one generation (an entered session, a
   * heartbeat, per-read generations). Runs after the generation moved and
   * before its requests and presented revisions are rejected. */
  readonly onInvalidate?: () => void;
  /** After the connection changed (a disconnect has already retired the generation). */
  readonly onConnection?: (next: ScreenConnection) => void;
  /** An applied board that comes back online after a disconnect (even one
   * the shell verified on the way back; never after a verification alone)
   * recovers itself. A first open the screen never finished re-issues its own
   * fenced call instead (`opening`). */
  readonly onReconnect?: () => void;
  /** The synchronous part of an unmount (a heartbeat stops at once). */
  readonly onUnmount?: () => void;
}

interface GuardOptions<Value, Tag extends string> {
  /** Lets `cancel(tags)` retire one family of reads without a new generation
   * (PM-19: a screen's read fencing is these tags and `tagEpoch`, nothing else). */
  readonly tag?: Tag;
  /** Request-specific admission (History: the exact permission, the selection
   * the read belongs to). Checked before dispatch and again on completion. */
  readonly admit?: () => boolean;
  /** A current completion, before the caller sees it. */
  readonly received?: (value: Value, requestEpoch: number) => void;
  /** A rejected request; `current` says whether its generation still holds. */
  readonly failed?: (error: unknown, current: boolean) => void;
  /**
   * The read's identity (its operation and parameters, such as the period it
   * reads): an identical read of the same tag and generation already in
   * flight is joined instead of sent again. The
   * shared request is aborted only when every caller that joined it retired.
   */
  readonly key?: string;
  /**
   * `'generation'` (default): the screen's own reset (`invalidate`) retires
   * the call. `'scope'`: only what ends the scope itself does — a real
   * unmount, a disconnect or the scope's admission. The operational presence of a screen (enter and
   * heartbeat) is fenced by principal, mount and connectivity, never by the
   * generations its ordinary commands move.
   */
  readonly lifetime?: 'generation' | 'scope';
}

interface SharedRead {
  readonly epoch: number;
  readonly tagEpoch: number;
  readonly controller: AbortController;
  readonly promise: Promise<unknown>;
  callers: number;
}

interface Receipt {
  readonly epoch: number;
  readonly resolve: () => void;
  readonly reject: (error: Error) => void;
  /** A revision React coalesced into a later commit still counts when the
   * committed view shows everything it presented. */
  readonly visible?: (view: unknown) => boolean;
}

export function createScopedScreenRuntime<Tag extends string = string>(options: ScopedScreenRuntimeOptions) {
  const { discarded } = options;
  let mounted = false;
  let connection: ScreenConnection = options.connection ?? 'online';
  // A real disconnect lasts until the scope is online again, whatever came
  // between: going back online makes the shell verify Identity first, so the
  // return is reconnecting, verifying, then online.
  let disconnected = connection === 'reconnecting';
  let epoch = 0;
  let revision = 0;
  let applied = false;
  let decisions = 0;
  // What ends the scope itself (a real unmount, a disconnect): it retires scope-lifetime calls too.
  let scopeEpoch = 0;
  // Every generation change or connection change: an opening call that met
  // one may be re-issued once the scope is whole again.
  let interruptions = 0;
  const shared = new Map<string, SharedRead>();
  const pending = new Map<() => void, Readonly<{ tag: Tag | undefined; lifetime: 'generation' | 'scope' }>>();
  const tagEpochs = new Map<Tag, number>();
  const receipts = new Map<number, Receipt>();
  const stamps = new WeakMap<object, number>();
  const attempts = new Map<string, Readonly<{ epoch: number; promise: Promise<unknown> }>>();
  let openingAttempt: Readonly<{ interruptions: number; promise: Promise<unknown> }> | null = null;

  const live = (requestEpoch?: number) => mounted && connection === 'online'
    && (requestEpoch === undefined || requestEpoch === epoch) && (options.admit?.() ?? true);
  // A guarded call may be dispatched, and its answer kept, while the shell
  // verifies Identity: a read waits for the verification inside Identity, a
  // mutation is refused there. Offline or a session that is not ready is not.
  const dispatchable = () => mounted && connection !== 'reconnecting';

  // A frozen scope is not a closed one. These waiters hold an opening call
  // while the app re-verifies Identity, and are released by the return to
  // `online` (or refused by a real unmount or by the resume window).
  const waiters = new Set<(restored: boolean) => void>();
  const settleWaiters = (restored: boolean) => {
    const waiting = [...waiters];
    waiters.clear();
    for (const waiter of waiting) waiter(restored);
  };
  const restored = (): Promise<boolean> => {
    if (live()) return Promise.resolve(true);
    if (!mounted) return Promise.resolve(false);
    return new Promise<boolean>((resolve) => {
      let timer = 0;
      const waiter = (value: boolean) => { window.clearTimeout(timer); resolve(value); };
      timer = window.setTimeout(() => { waiters.delete(waiter); resolve(false); }, SCREEN_OPEN_RESUME_MS);
      waiters.add(waiter);
    });
  };

  const retire = (scopeEnded: boolean) => {
    epoch += 1;
    interruptions += 1;
    if (scopeEnded) scopeEpoch += 1;
    options.onInvalidate?.();
    for (const [cancel, call] of [...pending]) if (scopeEnded || call.lifetime === 'generation') cancel();
    for (const receipt of receipts.values()) receipt.reject(discarded());
    receipts.clear();
  };
  /** The screen's own reset: a new generation. Scope-lifetime calls survive it. */
  const invalidate = () => retire(false);

  return {
    epoch: () => epoch,
    mounted: () => mounted,
    /** Mounted, online, admitted and (with an epoch) still in that generation. */
    live,
    /** A read may be dispatched now: mounted, admitted and not disconnected
     * (a foreground verification does not stop reads; it does stop `live`). */
    reading: () => dispatchable() && (options.admit?.() ?? true),
    /** Still mounted and in that generation, whatever the connection. */
    current: (requestEpoch: number) => mounted && requestEpoch === epoch,
    /** A presented revision has been committed in this scope (the board exists). */
    applied: () => applied,
    discarded,
    /**
     * Runs a port call under the scope's fences. A late or retired completion
     * rejects with `discarded()` and is never handed to the caller. `request`
     * receives the call's signal: a read passes it to its port, so retiring
     * the call (a new generation, its tag cancelled or superseded, an
     * unmount) aborts the fetch. A verification refuses neither the dispatch
     * nor the answer; an answer that lands while disconnected is discarded.
     */
    guard: <Value,>(request: (signal: AbortSignal) => Promise<Value>, guard: GuardOptions<Value, Tag> = {}): Promise<Value> => {
      const requestEpoch = epoch;
      const requestScopeEpoch = scopeEpoch;
      const lifetime = guard.lifetime ?? 'generation';
      const held = () => dispatchable() && (lifetime === 'scope' ? requestScopeEpoch === scopeEpoch : requestEpoch === epoch)
        && (options.admit?.() ?? true) && (guard.admit?.() ?? true);
      if (!held()) return Promise.reject(discarded());
      return new Promise<Value>((resolve, reject) => {
        let finished = false;
        // Leaves the request: aborts it, or leaves a shared read to its other callers.
        let leave: (reason: Error) => void = () => undefined;
        const finish = (complete: () => void) => {
          if (finished) return;
          finished = true;
          pending.delete(cancel);
          complete();
        };
        const cancel = () => finish(() => { const reason = discarded(); leave(reason); reject(reason); });
        pending.set(cancel, { tag: guard.tag, lifetime });
        const dispatch = (): Promise<Value> => {
          if (guard.key === undefined) {
            const controller = new AbortController();
            leave = (reason) => controller.abort(reason);
            return request(controller.signal);
          }
          const slot = JSON.stringify([guard.tag ?? null, guard.key]);
          const tagEpoch = guard.tag === undefined ? 0 : tagEpochs.get(guard.tag) ?? 0;
          let read = shared.get(slot);
          if (read === undefined || read.epoch !== requestEpoch || read.tagEpoch !== tagEpoch) {
            const controller = new AbortController();
            const promise: Promise<unknown> = request(controller.signal)
              .finally(() => { if (shared.get(slot) === entry) shared.delete(slot); });
            const entry: SharedRead = { epoch: requestEpoch, tagEpoch, controller, promise, callers: 0 };
            shared.set(slot, entry);
            read = entry;
          }
          const joined = read;
          joined.callers += 1;
          let left = false;
          const depart = () => { if (left) return false; left = true; joined.callers -= 1; return joined.callers === 0; };
          leave = (reason) => {
            if (!depart()) return;
            if (shared.get(slot) === joined) shared.delete(slot);
            joined.controller.abort(reason);
          };
          return (joined.promise as Promise<Value>).finally(depart);
        };
        void Promise.resolve().then(() => {
          if (finished || !held()) throw discarded();
          return dispatch();
        }).then((value) => {
          if (!held()) { cancel(); return; }
          finish(() => { guard.received?.(value, requestEpoch); resolve(value); });
        }, (error: unknown) => finish(() => { guard.failed?.(error, held()); reject(error); }));
      });
    },
    /** Retires the in-flight calls of these tags without a new generation,
     * and moves each tag's epoch so a read queued before it is known retired. */
    cancel: (tags: readonly Tag[]) => {
      for (const tag of tags) tagEpochs.set(tag, (tagEpochs.get(tag) ?? 0) + 1);
      for (const [cancel, { tag }] of [...pending]) if (tag !== undefined && tags.includes(tag)) cancel();
    },
    /** How many times `cancel` retired this tag: a read started at another
     * value belongs to a retired family and must not present anything. */
    tagEpoch: (tag: Tag): number => tagEpochs.get(tag) ?? 0,
    /**
     * PS-37 · the newest read of a family: retires the reads of this tag and
     * returns the check of the new one, true while it is still the newest of
     * its tag, in this generation, of a mounted scope.
     */
    supersede: (tag: Tag): (() => boolean) => {
      const next = (tagEpochs.get(tag) ?? 0) + 1;
      tagEpochs.set(tag, next);
      for (const [cancel, call] of [...pending]) if (call.tag === tag) cancel();
      const requestEpoch = epoch;
      return () => mounted && requestEpoch === epoch && tagEpochs.get(tag) === next;
    },
    /** Stamps a canonical result with the generation that received it. */
    stamp: (canonical: object, requestEpoch: number) => { stamps.set(canonical, requestEpoch); },
    /** The canonical result was received in the current generation. */
    stamped: (canonical: object) => stamps.get(canonical) === epoch,
    invalidate,
    /** Presents a new revision and resolves once React committed exactly it,
     * or (with `visible`) a later revision that still shows it. */
    commit: (present: (revision: number, epoch: number) => void, visible?: (view: unknown) => boolean) => {
      const next = ++revision;
      const receiptEpoch = epoch;
      return new Promise<void>((resolve, reject) => {
        receipts.set(next, { epoch: receiptEpoch, resolve, reject, ...(visible ? { visible } : {}) });
        present(next, receiptEpoch);
      });
    },
    /** A revision with no receipt (a failure surface replaces the board). */
    nextRevision: () => ++revision,
    /** The committed revision: resolves its receipt, rejects every superseded
     * one (unless the committed `view` still shows what it presented). */
    settle: (committed: number, committedEpoch = epoch, view?: unknown) => {
      for (const [receiptRevision, receipt] of receipts) {
        if (receiptRevision > committed) continue;
        receipts.delete(receiptRevision);
        const current = mounted && receipt.epoch === epoch && committedEpoch === epoch;
        if (current && (receiptRevision === committed || (view !== undefined && receipt.visible?.(view) === true))) {
          applied = true;
          receipt.resolve();
        } else receipt.reject(discarded());
      }
    },
    /**
     * One attempt per slot and generation. A call in the same generation joins
     * the running attempt; a call in a newer generation starts a new one and
     * receives the older attempt's promise, so it can wait for it to release an
     * exact command before retrying it.
     */
    attempt: <Outcome,>(slot: string, start: (requestEpoch: number, previous: Promise<Outcome> | undefined) => Promise<Outcome>) => {
      const existing = attempts.get(slot) as Readonly<{ epoch: number; promise: Promise<Outcome> }> | undefined;
      if (existing?.epoch === epoch) return existing.promise;
      const requestEpoch = epoch;
      const promise: Promise<Outcome> = start(requestEpoch, existing?.promise).finally(() => {
        if (attempts.get(slot)?.promise === promise) attempts.delete(slot);
      });
      attempts.set(slot, { epoch: requestEpoch, promise });
      return promise;
    },
    attempting: (slot: string) => attempts.get(slot)?.epoch === epoch,
    /**
     * QA 19 · the app re-verifies Identity on focus and the composition re-reads
     * the published configuration; either freezes the screen for a moment, and
     * the freeze retires the scope under the call that is opening it. The
     * answer the server had already sent was then discarded as stale, and
     * because nothing had been applied yet `connect('online')` had no board to
     * recover — so the first open reported a failure the server never sent.
     *
     * An opening call that only a scope change fenced is therefore re-issued
     * once, when the scope is whole again. A verification does not retire the
     * generation, but it still counts as such a change here. `fenced` says whether an outcome can
     * be a fence at all; a decision recorded with `decide()` meanwhile, an
     * applied board, an unmount or a resume window that ran out keeps it.
     * Nothing is assumed about the new scope: the re-issued call crosses every
     * fence again, so a session that really changed, or a canonical refusal —
     * a decision, not a fence — still fails closed.
     */
    opening: <Outcome,>(run: () => Promise<Outcome>, reissue: () => Promise<Outcome>, fenced: (outcome: Outcome) => boolean) => {
      const requestInterruptions = interruptions;
      const decided = decisions;
      const resume = async (outcome: Outcome): Promise<Outcome> => {
        if (!fenced(outcome) || applied || !mounted || interruptions === requestInterruptions || decisions !== decided) return outcome;
        if (!await restored()) return outcome;
        // The screen may already own a call in the scope that replaced ours.
        // Join it instead of sending a second call for the same first open.
        const concurrent = openingAttempt;
        if (concurrent !== null && concurrent.interruptions === interruptions) return concurrent.promise as Promise<Outcome>;
        return reissue();
      };
      const promise: Promise<Outcome> = Promise.resolve().then(run).then(resume)
        .finally(() => { if (openingAttempt?.promise === promise) openingAttempt = null; });
      openingAttempt = { interruptions: requestInterruptions, promise };
      return promise;
    },
    /** A refusal or an error reached the screen: not a fence. */
    decide: () => { decisions += 1; },
    mount: () => { mounted = true; if (live()) settleWaiters(true); },
    dispose: () => {
      mounted = false;
      options.onUnmount?.();
      // React's development effect replay immediately remounts this same
      // owner. A real unmount still rejects every later completion: `mounted`
      // already fences them, and the generation retires on the next microtask.
      queueMicrotask(() => { if (!mounted) { retire(true); settleWaiters(false); } });
    },
    connect: (next: ScreenConnection) => {
      if (next === connection) return;
      connection = next;
      // A real disconnect (offline, or a session that is not ready) retires
      // the scope; a foreground verification never does.
      if (next === 'reconnecting') { disconnected = true; retire(true); }
      else interruptions += 1;
      options.onConnection?.(next);
      if (next !== 'online') return;
      // Only a return from a real disconnect recovers the board, checked on
      // the way back or not; the end of a verification alone lost nothing.
      const returned = disconnected;
      disconnected = false;
      if (applied && returned) options.onReconnect?.();
      // A first open that this screen never finished is released here to
      // re-issue the one call the freeze fenced.
      settleWaiters(true);
    },
  };
}

/** PS-37 · a React owner's own scope, mounted and disposed with it: the
 * screens without a connection fence of their own (the tenant directories). */
export function useScreenScope<Tag extends string = string>(screen: string): ReturnType<typeof createScopedScreenRuntime<Tag>> {
  const [scope] = useState(() => createScopedScreenRuntime<Tag>({ discarded: () => new Error(`${screen}_scope_not_current`) }));
  useScopedScreenRuntime(scope);
  return scope;
}

/** Mount, unmount and the composition's connection fence, in that order. */
export function useScopedScreenRuntime(
  scope: Pick<ReturnType<typeof createScopedScreenRuntime>, 'mount' | 'dispose' | 'connect'>,
  connection?: ScreenConnection,
): void {
  useLayoutEffect(() => { scope.mount(); return scope.dispose; }, [scope]);
  useLayoutEffect(() => { if (connection !== undefined) scope.connect(connection); }, [scope, connection]);
}

export interface LiveRefreshTarget {
  /** A window focus or a visible tab. */
  readonly foreground: () => void;
  /** Every interval while the tab is visible. */
  readonly tick: () => void;
  /** Every focus and visibility change, visible or not (the presence heartbeat). */
  readonly presenceChanged: () => void;
}

/**
 * The read loop of a live board: an interval tick and the foreground, both
 * skipped while the tab is hidden (T-01). The Panel is poll-based by decision
 * (D-CLEANUP-13); what a tick and a foreground do is the screen's choice.
 * An interval that is not a positive finite number turns the loop off (a
 * screen that is not live, or a proof that drives its reads by hand).
 */
export function useLiveRefresh(target: LiveRefreshTarget, intervalMs: number): void {
  useEffect(() => {
    if (!Number.isFinite(intervalMs) || intervalMs <= 0) return undefined;
    const visible = () => document.visibilityState !== 'hidden';
    const foreground = () => { target.presenceChanged(); if (visible()) target.foreground(); };
    const tick = () => { if (visible()) target.tick(); };
    window.addEventListener('focus', foreground);
    document.addEventListener('visibilitychange', foreground);
    const timer = window.setInterval(tick, intervalMs);
    return () => {
      window.removeEventListener('focus', foreground);
      document.removeEventListener('visibilitychange', foreground);
      window.clearInterval(timer);
    };
  }, [target, intervalMs]);
}

/**
 * The presentation clock of a screen (relative ages such as «hace 2 min»):
 * `now()` at mount and on every interval. It reads nothing from the server.
 */
export function useScreenClock(now: () => number, intervalMs: number): number {
  const [value, setValue] = useState(now);
  useEffect(() => {
    setValue(now());
    const timer = window.setInterval(() => setValue(now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [now, intervalMs]);
  return value;
}

type PresenceRenewCommand = Extract<CoreExactMutation, { operation: 'workforce.operational-presence.renew.v1' }>;
type PresenceRenewResult = Readonly<{ kind: 'success'; canonical: WorkforceOperationalSessionResult }> | Readonly<{ kind: 'canonical_refusal' }>;

/**
 * D-70 · the foreground heartbeat of an entered operational session. Only a
 * visible, entered scope renews, and only an answer for this restaurant counts.
 */
export function createScopedPresenceHeartbeat(input: Readonly<{
  restaurantId: string;
  /** The scope is live and its operational session is entered. */
  entered: () => boolean;
  renew: (command: PresenceRenewCommand) => Promise<PresenceRenewResult>;
  createIdempotencyKey: (input: Readonly<{ operation: PresenceRenewCommand['operation']; restaurantId: string }>) => string;
  discarded: () => Error;
  onFailure: () => void;
}>) {
  const operation = 'workforce.operational-presence.renew.v1' as const;
  const { restaurantId } = input;
  return createOperationalPresenceHeartbeat({
    foreground: () => input.entered() && document.visibilityState !== 'hidden',
    renew: async () => {
      const result = await input.renew({
        operation, params: { restaurantId }, body: { signal: 'foreground_heartbeat' },
        idempotencyKey: input.createIdempotencyKey({ operation, restaurantId }),
      });
      if (result.kind !== 'success' || result.canonical.contribution.restaurant_id.toLowerCase() !== restaurantId.toLowerCase()) {
        throw input.discarded();
      }
    },
    onFailure: input.onFailure,
  });
}

interface RetainedFields<Outcome> {
  readonly signature: string;
  /** The exact original command: a replay reuses its request identity. */
  readonly invoke: () => Promise<Outcome>;
  pending: Promise<Outcome> | null;
  /** The operation outlived an access or scope change and waits for an
   * explicit recovery; no other intent is admitted meanwhile. */
  recovery: boolean;
}
export type RetainedOperation<Meta, Outcome> = Meta & RetainedFields<Outcome>;

/**
 * T-02 / H-01 · one retained operation. The same intent replays its original
 * command (same key); a different intent is refused while it is unresolved,
 * and so is every intent while it waits for recovery. A definitive answer
 * releases it. A screen that needs one per table keeps one slot per table.
 */
export function createRetainedOperation<Meta extends object, Outcome>(input: Readonly<{
  /** The outcome of an invocation that threw: its effect is unknown. */
  thrown: () => Outcome;
  /** The outcome leaves the operation retained. */
  unresolved: (outcome: Outcome) => boolean;
  /** Runs once a current entry has its answer, after a resolved one was released. */
  settled: (entry: RetainedOperation<Meta, Outcome>, outcome: Outcome, resolved: boolean) => void;
  /** Runs when an invocation starts. */
  started?: (entry: RetainedOperation<Meta, Outcome>) => void;
  /**
   * Invoke in the caller's tick instead of the next microtask: a board whose
   * tap must reach its controller at once (and draw busy in the same render)
   * asks for it. The default stays deferred.
   */
  immediate?: boolean;
}>) {
  let retained: RetainedOperation<Meta, Outcome> | null = null;
  const execute = (entry: RetainedOperation<Meta, Outcome>): Promise<Outcome> => {
    if (entry.pending) return entry.pending;
    const invoked: Promise<Outcome> = input.immediate === true
      ? new Promise<Outcome>((resolve) => { resolve(entry.invoke()); }) : Promise.resolve().then(entry.invoke);
    const pending: Promise<Outcome> = invoked.catch(input.thrown)
      .then((outcome) => {
        if (retained !== entry) return outcome;
        const resolved = !input.unresolved(outcome);
        if (resolved) retained = null;
        input.settled(entry, outcome, resolved);
        return outcome;
      }).finally(() => { if (entry.pending === pending) entry.pending = null; });
    entry.pending = pending;
    input.started?.(entry);
    return pending;
  };
  return {
    current: () => retained,
    /** H-01 · an operation that survived a reload comes back awaiting recovery. */
    restore: (entry: Meta & Readonly<{ signature: string; invoke: () => Promise<Outcome> }>) => {
      retained = { ...entry, pending: null, recovery: true };
    },
    /** The entry to execute for this intent, or null while another one is retained. */
    claim: (signature: string, create: () => Meta & Readonly<{ invoke: () => Promise<Outcome> }>): RetainedOperation<Meta, Outcome> | null => {
      if (retained && (retained.signature !== signature || retained.recovery)) return null;
      retained ??= { ...create(), signature, pending: null, recovery: false };
      return retained;
    },
    execute,
    /** A read answered the operation (achieved, superseded or impossible): it
     * is no longer retained, and its late answer settles nothing. */
    release: (entry: RetainedOperation<Meta, Outcome>) => { if (retained === entry) retained = null; },
  };
}

/** What a received answer means for its retained attempt. */
export type RetainedAttemptResolution<Outcome> = Readonly<{
  readonly outcome: Outcome;
  /** False keeps the exact command and the answer already received for a retry. */
  readonly resolved: boolean;
  /** With `resolved: false`: drop the answer and keep only the exact command,
   * so the retry sends it again with the same key (a refused or mismatched
   * credential recovery is asked again, never under a new key). */
  readonly resend?: true;
}>;

export interface RetainedAttemptRun<Response, Outcome> {
  /** One unresolved intent per resource: another intent on it is a conflict. */
  readonly resource: string;
  /** The exact intent. The same one replays the retained command and answer. */
  readonly signature: string;
  /** Builds the exact command once: its idempotency key and resource ids. */
  readonly prepare: () => () => Promise<Response>;
  /** Reads an answer: its outcome and whether it releases the attempt. */
  readonly resolve: (response: Response) => Promise<RetainedAttemptResolution<Outcome>>;
  /** `prepare` threw: nothing was sent and nothing is retained. */
  readonly unprepared: () => Outcome;
  /** The request threw: its effect is unknown, the command is retained. */
  readonly thrown: () => Outcome;
  /** `resolve` threw over a received answer, which stays retained. */
  readonly unresolvable: (response: Response) => Outcome;
  /**
   * H-01 · an identity kept beyond this screen (History's refund survives a
   * reload in the tab's session storage). While it holds another signature
   * every other intent is a conflict; a resolution releases it.
   */
  readonly persisted?: Readonly<{ signature: () => string | null; release: () => void }>;
  /** Runs whenever an execution of the attempt starts (not for a joined double tap). */
  readonly started?: () => void;
}

interface RetainedAttemptFacts {
  readonly execute: () => Promise<unknown>;
  readonly resolve: (response: unknown) => Promise<RetainedAttemptResolution<unknown>>;
  readonly unresolvable: (response: unknown) => unknown;
  readonly thrown: () => unknown;
  readonly persisted: RetainedAttemptRun<unknown, unknown>['persisted'];
  readonly started: RetainedAttemptRun<unknown, unknown>['started'];
  answer: Readonly<{ response: unknown }> | null;
}

/**
 * Q-17 · PM-18 · PS-14 · the one retained attempt of the Panel bridges
 * (TPV keeps its table slots in its operation queue, on the same primitive).
 * Each resource holds one `createRetainedOperation`: the first call of an
 * intent builds its exact command once; a double tap joins the running call;
 * an answer that does not resolve the intent (a transport failure, a
 * re-read that failed) keeps the command and the answer already received, so
 * the retry replays the same idempotency key, or re-reads without sending
 * again. A resolved attempt is released, and the next intent builds anew.
 * Every outcome passes through `notify` before its attempt is released.
 */
export function createRetainedAttempts<Outcome>(input: Readonly<{
  notify: (outcome: Outcome) => Promise<Outcome>;
  /** Another intent is retained on this resource (or persisted beyond it). */
  conflict: () => Outcome;
  /** Send in the caller's tick (`createRetainedOperation`'s `immediate`). */
  immediate?: boolean;
}>) {
  type Wrapped = RetainedAttemptResolution<Outcome>;
  type Slot = ReturnType<typeof createRetainedOperation<RetainedAttemptFacts, Wrapped>>;
  const slots = new Map<string, Slot>();
  const joined = new WeakMap<Promise<Wrapped>, Promise<Outcome>>();

  const invoke = async (facts: RetainedAttemptFacts): Promise<Wrapped> => {
    if (facts.answer === null) {
      try { facts.answer = { response: await facts.execute() }; }
      catch { return { outcome: await input.notify(facts.thrown() as Outcome), resolved: false }; }
    }
    const { response } = facts.answer;
    let resolution: RetainedAttemptResolution<unknown>;
    try { resolution = await facts.resolve(response); }
    catch { return { outcome: await input.notify(facts.unresolvable(response) as Outcome), resolved: false }; }
    if (resolution.resolved) facts.persisted?.release();
    else if (resolution.resend) facts.answer = null;
    return { outcome: await input.notify(resolution.outcome as Outcome), resolved: resolution.resolved };
  };

  const slotOf = (resource: string): Slot => {
    const existing = slots.get(resource);
    if (existing !== undefined) return existing;
    const slot: Slot = createRetainedOperation<RetainedAttemptFacts, Wrapped>({
      // `invoke` answers every failure itself; this is only the primitive's floor.
      thrown: () => ({ outcome: input.conflict(), resolved: false }),
      unresolved: (wrapped) => !wrapped.resolved,
      settled: (_entry, _wrapped, resolved) => { if (resolved && slots.get(resource) === slot) slots.delete(resource); },
      started: (entry) => entry.started?.(),
      ...(input.immediate === true ? { immediate: true } : {}),
    });
    slots.set(resource, slot);
    return slot;
  };

  // A double tap joins the running call and receives the very same promise.
  const execute = (slot: Slot, entry: RetainedOperation<RetainedAttemptFacts, Wrapped>): Promise<Outcome> => {
    const pending = slot.execute(entry);
    let outcome = joined.get(pending);
    if (outcome === undefined) {
      outcome = pending.then((wrapped) => wrapped.outcome);
      joined.set(pending, outcome);
    }
    return outcome;
  };

  return {
    run: <Response,>(run: RetainedAttemptRun<Response, Outcome>): Promise<Outcome> => {
      const held = run.persisted?.signature() ?? null;
      if (held !== null && held !== run.signature) return input.notify(input.conflict());
      const slot = slotOf(run.resource);
      let entry: RetainedOperation<RetainedAttemptFacts, Wrapped> | null;
      try {
        entry = slot.claim(run.signature, () => {
          const facts: RetainedAttemptFacts = {
            execute: run.prepare(),
            resolve: run.resolve as RetainedAttemptFacts['resolve'],
            unresolvable: run.unresolvable as RetainedAttemptFacts['unresolvable'],
            thrown: run.thrown,
            persisted: run.persisted,
            started: run.started,
            answer: null,
          };
          return { ...facts, invoke: () => invoke(entry!) };
        });
      } catch {
        if (slot.current() === null && slots.get(run.resource) === slot) slots.delete(run.resource);
        return input.notify(run.unprepared());
      }
      if (entry === null) return input.notify(input.conflict());
      return execute(slot, entry);
    },
    /** An unresolved (or running) attempt holds this resource. */
    held: (resource: string): boolean => slots.get(resource)?.current() != null,
    /** Replays the attempt retained on this resource, if any, with its exact command. */
    retry: (resource: string): Promise<Outcome> | null => {
      const slot = slots.get(resource);
      const entry = slot?.current() ?? null;
      return slot === undefined || entry === null ? null : execute(slot, entry);
    },
  };
}
